/** TWILIO_WEBHOOK_PUSH_CONFIG control: show the Conversations post-event webhook URL + push config
 *  (live), and reconfigure them via the execute endpoint (sets the target URL + filters + push
 *  templates/sound/badge). "Show Twilio webhook URLs and allow to reconfigure them."
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { PreflightItem, TwilioWebhookDetail } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

export function TwilioWebhookControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const seed = item.detail as unknown as TwilioWebhookDetail;
  const query = useQuery({
    queryKey: ['preflight', 'twilioWebhookStatus', topId, item.id],
    queryFn: () => items.fetchTwilioWebhookStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;

  // When the live probe confirms the post-event webhook, the backend advances past the webhook
  // step; refetch the list so the row reflects it (guarded on item.step so it runs once).
  useEffect(() => {
    if (status?.ok && status.detail?.webhook_ok && item.step < 1) {
      onChanged();
    }
  }, [status, item.step, onChanged]);

  const configure = useMutation({
    mutationFn: () => items.execute(topId, item.id),
    onSuccess: (res) => {
      const run = res.run;
      if (run && run.status === 'error') {
        snackbar.show(run.error_message || 'Could not configure webhooks & push.', 'error');
      } else {
        snackbar.show(run?.summary || 'Webhooks & push configured.', 'success');
      }
      void query.refetch();
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not configure webhooks & push.', 'error'),
  });

  if (status?.reason === 'not_configured' || detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">Twilio isn&apos;t configured on the server.</Alert>
      </Box>
    );
  }

  const pushTemplate =
    detail?.push && typeof detail.push['new_message_template'] === 'string'
      ? (detail.push['new_message_template'] as string)
      : null;
  const webhookOk = detail?.webhook_ok === true;
  const filtersOk = detail?.filters_ok === true;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {detail?.error && (
          <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {detail.error}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 1.5,
            rowGap: 0.5,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Target webhook
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            <ValueActions actions={copyOnlyActions(detail?.target_webhook_url, 'Copy target URL')}>
              {detail?.target_webhook_url || '—'}
            </ValueActions>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current webhook
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {webhookOk ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
            )}
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              <ValueActions actions={copyOnlyActions(detail?.post_webhook_url, 'Copy current URL')}>
                {detail?.post_webhook_url || '(none)'}
              </ValueActions>
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Filters
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            {(detail?.filters ?? []).map((f) => (
              <Chip key={f} size="small" variant="outlined" label={f} />
            ))}
            {!filtersOk && (
              <Chip size="small" color="warning" variant="outlined" label="missing required filters" />
            )}
          </Stack>
          {pushTemplate && (
            <>
              <Typography variant="caption" color="text.secondary">
                Push text
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {pushTemplate}
              </Typography>
            </>
          )}
        </Box>

        {canManage && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={() => configure.mutate()}
              disabled={configure.isPending}
              startIcon={configure.isPending ? <CircularProgress size={14} /> : undefined}
            >
              {webhookOk && filtersOk ? 'Reconfigure webhooks & push' : 'Configure webhooks & push'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              Refresh
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
